import Vue from 'vue'
import Vuex from 'vuex'
import actions from './actions.js'
import mutations from './mutations.js'
import colors from './colors.js'
import pluginsDirectory from './pluginsDirectory.js'
const fb = require('../firebaseConfig.js')

Vue.use(Vuex)

// handle page reload
fb.auth.onAuthStateChanged(user => {
  if (user) {
    store.commit('setCurrentUser', user)
    store.dispatch('fetchUserProfile')
    fb.usersCollection.doc(user.uid).onSnapshot(doc => {
      store.commit('setUserProfile', doc.data())
    }, err => {
      // Handle listener errors (e.g. a transient permission/token blip on
      // sign-in) so they don't surface as an uncaught error in the console.
      console.warn('User profile listener error:', err && err.message)
    })
  }
})

export const store = new Vuex.Store({
  state: {
    alert: {
      active: false,
      level: 'alert-info',
      message: 'This is a basic alert'
    },
    // When true, the AVIRIS-3 tab is in point-sampling mode — the
    // blocksLayer click handler bails so a map click samples a spectrum
    // instead of navigating to a block.
    avirisSampling: false,
    block: {},
    blocks: [],
    blocksGeoJson: {},
    breadcrumbs: [
      {
        id: 'root',
        name: 'Root'
      }
    ],
    collector: {
      fields: []
    },
    collectors: [],
    colors: colors,
    createFolderPrompt: false,
    currentUser: null,
    dataPoint: {},
    dataset: {},
    datasetGeoJson: {},
    datasets: [],
    defaults: {
      pointStops: {
        stops: [[15, 1], [16, 2], [17, 3], [18, 6], [20, 20], [23, 50]]
      }
    },
    display: {
      datasetReady: false,
      drawer: {},
      editMode: false,
      fullscreen: false,
      mapClass: 'out',
      modal: {
        addBlockField: false,
        addField: false,
        collaborators: false,
        createBlock: false,
        createCollector: false,
        createDataset: false,
        createFarm: false,
        createMap: false,
        cropLoadCalculator: false,
        dataset: false,
        feedback: false,
        firstFarmModal: false,
        map: false,
        pluginLibrary: false,
        pluginModal: false,
        settings: false
      },
      processingDataset: false,
      showMap: false,
      unreadNotifications: 0
    },
    drawControlsObject: false,
    drawer: {
      apiTokens: {
        position: 'out',
        show: false
      },
      blockDetails: {
        position: 'out',
        show: false
      },
      blockRevisions: {
        position: 'out',
        show: false
      },
      collaborators: {
        position: 'out',
        show: false
      },
      collector: {
        position: 'out',
        show: false
      },
      collectors: {
        position: 'out',
        show: false
      },
      copyDataset: {
        position: 'out',
        show: false
      },
      createBlockDrawer: {
        position: 'out',
        show: false
      },
      createDataset: {
        position: 'out',
        show: false
      },
      createDatasetDrawer: {
        position: 'out',
        show: false
      },
      createFarm: {
        position: 'out',
        show: false
      },
      createMapDrawer: {
        position: 'out',
        show: false
      },
      dataPointDetails: {
        position: 'out',
        show: false
      },
      datasetDetails: {
        position: 'out',
        show: false
      },
      datasets: {
        position: 'out',
        show: false
      },
      farmDetails: {
        position: 'out',
        show: false
      },
      farms: {
        position: 'out',
        show: false
      },
      notifications: {
        position: 'out',
        show: false
      },
      plugins: {
        position: 'out',
        show: false
      },
      previewBlockRevision: {
        position: 'out',
        show: false
      },
      previewRevision: {
        position: 'out',
        show: false
      },
      revisions: {
        position: 'out',
        show: false
      },
      satellite: {
        position: 'out',
        show: false
      },
      userDetails: {
        position: 'out',
        show: false
      },
      userSettings: {
        position: 'out',
        show: false
      }
    },
    farm: {},
    farms: [],
    filterBy: '',
    folder: {
      id: 'root',
      name: 'Root'
    },
    folders: [],
    geocoderControl: {},
    hiddenDatasets: [],
    keyMode: 'blocks',
    keyStatsArea: 0,
    keyStatsLabel: '',
    keyStatsMax: 0,
    keyStatsMin: 0,
    keyStatsTotalArea: 0,
    lastVisibleDataset: {},
    // Live tracking (docs/live-tracking-phase1.md): count powers the header
    // pill; visible mirrors whether the map listeners/layers are active.
    liveTrackCount: 0,
    liveTracksVisible: false,
    loadingMap: false,
    locateControl: {},
    mapClickable: false,
    mapObject: {},
    moveMenuOpen: false,
    notifications: [],
    paginate: false,
    pauseMap: false,
    plugin: {
      arg1: '',
      name: '',
      url: ''
    },
    pluginsDirectory: pluginsDirectory,
    printingClass: 'no-print',
    rangeZones: [],
    role: '',
    selectBlocksListenersAdded: false,
    selectedBlocks: [],
    selectedDatapoints: [],
    selectMarker: false,
    showCreateBlockDrawer: false,
    // showDatasetVizSettings: false,
    showKey: true,
    showKeyStats: false,
    showRampSettings: true,
    sortBy: 'Most Recent',
    subLayerPicker: false,
    subLayerGeoJson: {},
    transferMenuOpen: false,
    userProfile: {},
    // Super-admin flag — derived from `admins/{uid}.admin === true`.
    // Loaded once on auth (see fetchUserProfile action) and used to
    // gate the /admin route + hide admin-only UI for normal users.
    // Defaults to false so any code path that reads this before auth
    // completes errs on the side of "not an admin".
    isSuperAdmin: false,
    // Super-admin "launch farm as role" simulation. When active, fetchFarm
    // applies `role` as the in-app role for `farmId` instead of the
    // super-admin's real role, so the whole UI re-gates as that role would
    // see it. CLIENT-SIDE ONLY: the security rules still grant super-admins
    // full access, so this simulates the *interface*, not backend authz.
    simulation: {
      active: false,
      role: '',
      farmId: '',
      farmName: ''
    },
    userLocation: [],
    viz: {
      classification: 'jenks',
      colorSetting: 'linear',
      header: false,
      interpolated: false,
      interpolatedMax: 1,
      interpolatedMin: 0,
      max: 1,
      min: 0,
      zones: 5
    },
    zoomControl: {},
    // Track if we're in datapoint selection mode
    datapointSelectionMode: false
  },
  mutations: mutations,
  actions: actions
})
